import { useEffect, useState } from "react"
import { Flex, Typography, Dropdown, Popover, Modal, Checkbox, Button, Form, Input, message } from "antd"
import { MoreOutlined, DeleteOutlined, ProjectOutlined, CopyOutlined, CheckOutlined } from "@ant-design/icons"
import { dbColorIconBgMapping, spaceColorBgMapping, spaceIconsConfig, workflowIconsConfig } from "../../../utils/colorBgColorMap"
import SvgIcons from "../../layout/assets/SvgIcons"
import { createDatabase, deleteMasterData, getAffectedSpaces } from "../../../services/adminServices"
import { showLoader } from "../../../utils/showloader"
import { trimStringValues } from "../../../utils/trimStrings"
import { v4 as uuidv4 } from "uuid";
import { useTenantNavigation } from "../../../hooks/useTenantNavigation.js"
import { useTranslation } from "react-i18next"
import { resolveMasterNameTranslation } from "../../../utils/masterTranslations.js"

const Title = Typography.Title


const DatabaseCard = (props) => {
    const navigate = useTenantNavigation()
    const { t, i18n } = useTranslation();
    const { data, getData, masterList } = props
    const [dbData, setDbData] = useState({})
    const [showCopyModal, setShowCopyModal] = useState(false)
    const [affectedModal, setAffectedModal] = useState(false)
    const [affectedSpaces, setAffectedSpaces] = useState([])
    const [deleteText, setDeleteText] = useState("")
    const [copyForm] = Form.useForm()
    
    useEffect(() => {
        setDbData({ ...data })
    }, [data])

    const navigateToTable = (tableId, tableName) => {
        navigate("/app/admin/master-data/tables/" + tableId + "?table=" + tableName)
    }

    const handleIconColorChange = (key, val) => {
        const updatedData = { ...dbData, [key]: val }
        setDbData(updatedData)
    }

    const updateIconColor = async () => {
        const updatedDbData = { ...dbData, data: [] }
        showLoader(true)
        const postResponse = await createDatabase(updatedDbData, true)
        showLoader(false)
        if (postResponse.status === 200) {
            message.success(postResponse.message)
            await getData()
        } else {
            message.error(postResponse.message)
        }
    }

    const colorIconPicker = (
        <div className="xl:w-94 lg:w-80 sm:w-36 md:w-56 color-icon-picker">
            <div className="flex justify-between items-center mb-4">
                <div className="font-semibold text-gray-800">{t('Pick color and icon')}</div>
                <Button
                    type="text"
                    icon={<CheckOutlined />}
                    size="small"
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    onClick={updateIconColor}
                />
            </div>
            <div className="color-row">
                {
                    ["#F5222D", "#FA8C16", "#FAAD14", "#84CC16", "#50A248", "#13C2C2", "#0A99FE", "#141BCB", "#722ED1", "#EB2F96"].map(dbColor => {
                        return <div className="color-box" style={{ backgroundColor: dbColor }} onClick={() => handleIconColorChange("dbColor", dbColor)}>{data.dbColor === dbColor && <CheckOutlined className="text-white text-sm" />}</div>
                    })
                }
            </div>
            <div className="icon-picker">
                {
                    spaceIconsConfig.map(icon => {
                        return <div className="icon-picker-box" onClick={() => handleIconColorChange("dbIcon", icon)}>
                            <span style={{ color: data.dbIcon === icon ? data.dbColor : "#00000073", display: "flex" }}>
                                <SvgIcons key={"color-" + data.stepColor} type={icon} />

                            </span>
                        </div>

                    })
                }
            </div>
        </div>
    )

    const confirmDatabaseCopy = () => {
        copyForm.setFieldsValue({ dataName: `${dbData.dataName} copy` })
        setShowCopyModal(true)
    }

    const handleMasterDelete = async () => {
        if(affectedSpaces?.length === 0) {
            let affectedResponse = await getAffectedSpaces(data.dataId)
            if (affectedResponse.data) {
                setAffectedSpaces(affectedResponse.data)
            }
        }
        setAffectedModal(true)
    }

    const confirmDatabaseDelete = async () => {
        setAffectedModal(false)
        setDeleteText("")
        showLoader(true)
        let deleteResponse = await deleteMasterData(data.dataId)
        showLoader(false)
        if (deleteResponse?.status === 200) {
            await getData()

        }
    }

    const databaseActions = [
        {
            key: "copy",
            label: (
                <Flex gap={8} className="w-28">
                    <CopyOutlined className="text-blue-500" />
                    <span className="text-gray-700">{t("Copy")}</span>
                </Flex>
            ),
            onClick: confirmDatabaseCopy
        },
        {
            key: "delete",
            label: (
                <Flex gap={8} className="w-28">
                    <DeleteOutlined className="text-red-500" />
                    <span className="text-gray-700">{t("Delete")}</span>
                </Flex>
            ),
            onClick: handleMasterDelete
        },
    ]

    const handleModalCancel = () => {
        setShowCopyModal(false)
        copyForm.resetFields()
    }

    const submitTableCopy = async (values) => {
        values = trimStringValues(values)
        const nameExists = masterList.find(data => data.dataName === values.dataName)
        if (nameExists) {
            message.error(`Table with name "${values.dataName}" already exists`)
            return
        }

        const dbFields = dbData.fields.map(field => ({
            ...field,
            id: uuidv4()
        }))

        const dbDataObj = {
            dataName: values.dataName,
            fields: dbFields
        }

        if (values.copyData) {
            dbDataObj.data = dbData.data
        } else {
            dbDataObj.data = []
        }

        showLoader(true)
        const postResponse = await createDatabase(dbDataObj)
        showLoader(false)

        if (postResponse.status === 200) {
            message.success(postResponse.message)
            await getData()
            handleModalCancel()
        } else {
            message.error(postResponse.message)
        }
    }

    const handleAffectedFormClose = () => {
        setAffectedModal(false)
        setDeleteText("")
    }


    // Display only — `dataName` stays canonical in the URL and in lookups. The card
    // truncates it to one line and shows the full name in a tooltip on hover.
    const displayName = resolveMasterNameTranslation(dbData, i18n.language)

    return (
        <div
            className="rounded-xl p-6 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105  w-64 h-40 flex flex-col"
            style={{ background: dbData.dbColor ? spaceColorBgMapping[dbData.dbColor] : "#E7F5FF" }}
        >
            <Flex justify="space-between" align="flex-start" className="mb-4">
                <Popover
                    content={colorIconPicker}
                    trigger="click"
                    placement="right"
                    overlayClassName="color-picker-popover"
                >
                    <div
                        className="w-10 h-10 rounded-lg cursor-pointer flex items-center justify-center shadow-sm hover:shadow-md transition-shadow duration-200"
                        style={{ background: dbData.dbColor ? dbColorIconBgMapping[dbData.dbColor] : "#c4e7ff" }}
                    >
                        {dbData?.dbIcon ? (
                            <span className="flex text-lg" style={{ color: dbData.dbColor || "#0A99FE" }}>
                                <SvgIcons key={`color-${dbData.dbColor}`} type={dbData.dbIcon} />
                            </span>
                        ) : (
                            <ProjectOutlined
                                className="text-lg"
                                style={{ color: dbData.dbColor || "#0a99fe" }}
                            />
                        )}
                    </div>
                </Popover>
                <Dropdown
                    trigger="click"
                    menu={{ items: databaseActions }}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        icon={<MoreOutlined />}
                        className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        size="small"
                    />
                </Dropdown>
            </Flex>

            <div
                onClick={() => navigateToTable(dbData.dataId, dbData.dataName)}
                className="space-y-2 flex-1 flex flex-col justify-center"
            >
                <Title
                    level={5}
                    className="font-semibold text-gray-800 mb-1 leading-tight hover:text-blue-600 transition-colors duration-200"
                    ellipsis={{ tooltip: displayName }}
                >
                    {displayName}
                </Title>
                {/* <p className="text-gray-500 text-sm mb-2 leading-relaxed">
                    {dbData.dataName} master data
                </p> */}
                <div className="text-blue-600 font-semibold text-sm">
                    {dbData.data?.length || 0} entries
                </div>
            </div>

            <Modal
                title={
                    <div className="flex items-center gap-2">
                        <CopyOutlined className="text-blue-500" />
                        <span>{t('Copy Master Data')}</span>
                    </div>
                }
                open={showCopyModal}
                footer={null}
                onCancel={handleModalCancel}
                className="copy-master-modal"
            >
                <Form
                    form={copyForm}
                    onFinish={submitTableCopy}
                    layout="vertical"
                    className="pt-4"
                >
                    <Form.Item
                        label={<span className="font-medium text-gray-700">{t('Master Data Name')}</span>}
                        name="dataName"
                        rules={[{ required: true, whitespace: true, message: "Master data name is required" }]}
                    >
                        <Input
                            placeholder={t('Enter master data name')}
                            size="large"
                            className="rounded-lg"
                        />
                    </Form.Item>

                    <Form.Item
                        name="copyData"
                        valuePropName="checked"
                        className="mb-4"
                    >
                        <Checkbox className="text-gray-700">
                            {t('Copy existing data along with structure')}
                        </Checkbox>
                    </Form.Item>

                    <div className="bg-blue-50 p-3 rounded-lg mb-6 border border-blue-200">
                        <p className="text-blue-700 text-sm italic mb-0">
                            <strong>{t('Note:')}</strong> {t('Table structure will be copied. Select the checkbox above to also copy existing data.')}
                        </p>
                    </div>

                    <Flex justify="flex-end" gap={8}>
                        <Button
                            onClick={handleModalCancel}
                            className="rounded-lg"
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="rounded-lg bg-blue-600 hover:bg-blue-700"
                        >
                            {t('Create Copy')}
                        </Button>
                    </Flex>
                </Form>
            </Modal>
            <Modal title={t('Affected Spaces and Forms')} open={affectedModal} onClose={handleAffectedFormClose} onCancel={handleAffectedFormClose} >
                {
                    affectedSpaces.length > 0 ?
                    <>
                        <div className="mb-3 text-[18]">{t('These are the spaces where "')}{data.dataName}" is used. <b>{t('Please delete the widget and conditions from the forms before deleting the table.')}</b></div>
                        <div className="affected-spaces">
                            {
                                affectedSpaces.map((space, index) => {
                                    return <Flex gap={8} align="center">
                                        <span className="step-circle">{index + 1}</span><div className="m-2">{space.formName} ({space.spaceName})</div>
                                    </Flex>
                                })
                            }
                        </div>
                    </>
                    : <div>{t('This table is not used in any spaces.')}</div>
                }
                {
                    affectedSpaces?.length === 0 ? 
                    <>
                        <div className="my-3">{t('Type')} <span className="text-red-500">delete</span> {t('to confirm')}</div>
                        <Input size="large" onChange={(e) => setDeleteText(e.target.value)} value={deleteText} placeholder="delete" className="mb-4" />
                        <Flex justify="flex-end" gap={16}>
                            <Button type="default">{t('Cancel')}</Button>
                            <Button className="submit-button" onClick={confirmDatabaseDelete} disabled={deleteText !== "delete"}>{t('Delete')}</Button>
                        </Flex>
                    </>
                    : null
                }
            </Modal>
        </div>
    )
}

export default DatabaseCard